import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CustomDateTimePicker from "../common/CustomDateTimePicker";
import { pickPrimaryWindow } from "../transfer-windows/utils";
import {
  formatScheduledPublish,
  formatRequestDate,
} from "../../utils/tenderFormatters";

/**
 * The schedule picker for a draft request: pick a date and a time, confirm,
 * or take an existing date off again.
 *
 * WHAT IS REUSED, AND FROM WHERE
 * ------------------------------
 *  - The dialog shell (portal, backdrop, ESC, bottom sheet on phones and a
 *    centred panel from md up) is the platform's own small-modal shape, taken
 *    from components/common/ContractExpiryModal.js:73-168 — the closest
 *    existing dialog, because it is also "one date, confirm, cancel".
 *  - The date and time controls are components/common/CustomDateTimePicker.js,
 *    used exactly the way the friendlies composer uses it
 *    (components/friendlies/FriendlyComposer.jsx:216-228): one instance for the
 *    day, a second with showTimeSelectOnly for the clock. No native
 *    <input type="date">, so the picker renders in English instead of the
 *    browser's OS locale.
 *  - The transfer-window reading is components/transfer-windows/utils.js
 *    pickPrimaryWindow(), the same function the transfer-windows page uses to
 *    decide whether a window is open, upcoming or closed. Nothing about
 *    windows is recomputed here.
 *
 * THE ONE DESIGN DELTA
 * --------------------
 * The hint line is the single element the handoff does not draw. It is built
 * in the dialog's own language (the calm blue helper box the platform already
 * uses for dialog hints) and it says only what is actually known: when no
 * window data has arrived, no line renders at all rather than a guess.
 */

// Pure, so the hint can be reasoned about without a DOM: turns the club
// requests page's transfer-window payload into the one sentence worth saying.
// Returns "" when nothing is known — the caller then renders no line.
export function transferWindowHint(windowData, now = new Date()) {
  if (!windowData) return "";
  const primary = pickPrimaryWindow([windowData], now);
  if (!primary) return "";
  if (primary.state === "open" && primary.end) {
    return `The current window closes ${formatRequestDate(primary.end)}.`;
  }
  if (primary.state === "opens" && primary.start) {
    return `The next window opens ${formatRequestDate(primary.start)}.`;
  }
  return "";
}

// Combine the chosen day and the chosen clock time into one moment. Built from
// LOCAL parts, like utils/friendlyDates.js does, so nothing shifts a day.
const combine = (day, time) => {
  if (!day) return null;
  const out = new Date(day.getTime());
  out.setHours(time ? time.getHours() : 9, time ? time.getMinutes() : 0, 0, 0);
  return out;
};

const ScheduleRequestModal = ({
  isOpen,
  onClose,
  onConfirm,
  onRemove,
  requestTitle = "",
  clubName = "",
  currentValue = null,
  transferWindow = null,
  isSaving = false,
}) => {
  const [day, setDay] = useState(null);
  const [time, setTime] = useState(null);

  // Reopening always starts from what is stored, never from the last edit.
  useEffect(() => {
    if (!isOpen) return;
    const existing = currentValue ? new Date(currentValue) : null;
    const valid = existing && !Number.isNaN(existing.getTime()) ? existing : null;
    setDay(valid);
    if (valid) {
      setTime(valid);
    } else {
      // Nine in the morning is the default the design confirms with
      // ("Goes live ... 09:00"), so an unset clock is not an empty field.
      const nine = new Date();
      nine.setHours(9, 0, 0, 0);
      setTime(nine);
    }
  }, [isOpen, currentValue]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !isSaving) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, isSaving, onClose]);

  if (!isOpen) return null;

  const chosen = combine(day, time);
  const hint = transferWindowHint(transferWindow);
  const hasExisting = Boolean(currentValue);
  const subtitle = [requestTitle, clubName].filter(Boolean).join(" · ");
  const portalTarget = document.getElementById("modal-root") || document.body;

  return createPortal(
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => !isSaving && onClose?.()}
      />
      <div className="relative flex min-h-full items-end justify-center p-4 md:items-center">
        <div className="w-full max-w-md max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white p-6 shadow-xl md:rounded-2xl">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-[#1A212F]">
                {hasExisting ? "Reschedule request" : "Schedule request"}
              </h2>
              {subtitle && (
                <p className="mt-1 text-sm text-[#657084] break-words">
                  {subtitle}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => !isSaving && onClose?.()}
              disabled={isSaving}
              aria-label="Close"
              className="min-h-[44px] min-w-[44px] -mr-2 -mt-2 flex items-center justify-center text-gray-400 hover:text-gray-600"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="sm:flex-[1.4] min-w-0">
              <CustomDateTimePicker
                value={day}
                onChange={setDay}
                label="Date"
                placeholder="Pick a date"
                minDate={new Date()}
                dateFormat="EEE, dd MMM yyyy"
                disabled={isSaving}
              />
            </div>
            <div className="sm:flex-1 min-w-0">
              <CustomDateTimePicker
                value={time}
                onChange={setTime}
                label="Time"
                placeholder="Pick a time"
                showTimeSelectOnly
                timeIntervals={15}
                dateFormat="HH:mm"
                disabled={isSaving}
                position="right-0"
              />
            </div>
          </div>

          {hint && (
            <div className="mt-4 rounded-lg bg-blue-50 p-3">
              <p className="text-sm text-blue-800">{hint}</p>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => chosen && onConfirm?.(chosen)}
              disabled={!chosen || isSaving}
              className={`min-h-[44px] w-full rounded-lg bg-[#2283FF] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-600 ${
                !chosen || isSaving
                  ? "cursor-not-allowed opacity-60 hover:bg-[#2283FF]"
                  : ""
              }`}
            >
              {isSaving
                ? "Saving..."
                : chosen
                  ? `Goes live ${formatScheduledPublish(chosen)}`
                  : "Pick a date"}
            </button>
            {hasExisting && (
              <button
                type="button"
                onClick={() => onRemove?.()}
                disabled={isSaving}
                className="min-h-[44px] w-full rounded-lg px-4 py-2 text-sm font-semibold text-[#FF4D4F] hover:bg-red-50 disabled:opacity-60"
              >
                Remove date
              </button>
            )}
            <button
              type="button"
              onClick={() => !isSaving && onClose?.()}
              disabled={isSaving}
              className="min-h-[44px] w-full rounded-lg border border-[#E6E6E6] px-4 py-2 text-sm font-medium text-[#1E1E1E] hover:bg-gray-50 disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>,
    portalTarget
  );
};

export default ScheduleRequestModal;
